import { useState } from 'react'
import { App, Empty, Input } from 'antd'
import { Search, ShieldCheck } from 'lucide-react'
import {
  permissionByCode,
  permissionClosure,
  permissionDefinitions,
  removePermissionDependents,
  type PermissionCode,
  type PermissionDefinition
} from '@super-mega/server/contracts/authorization'
import { cn } from '@/utils/index.ts'
import { PermissionModule } from './permission-module.tsx'

// 与业务导航保持一致；角色和机器人使用同一顺序，未登记模块追加到末尾。
const moduleOrder = [
  '报表',
  '客户',
  '服务对象',
  '订单',
  '财务',
  '合同',
  '基础资料',
  '成员',
  '账号',
  '系统授权',
  '机器人',
  '证件资料'
]

type Props = {
  value?: PermissionCode[]
  onChange?: (codes: PermissionCode[]) => void
  disabled?: boolean
  protectedRole?: boolean
  definitions?: readonly PermissionDefinition[]
}

export function PermissionPicker({
  value = [],
  onChange,
  disabled,
  protectedRole,
  definitions = permissionDefinitions
}: Props) {
  const { modal } = App.useApp()
  const [keyword, setKeyword] = useState('')
  const [activeModule, setActiveModule] = useState<string>('')
  const needle = keyword.trim().toLowerCase()
  const selected = new Set(value)
  const grouped = new Map<string, PermissionDefinition[]>()
  for (const definition of definitions) {
    if (needle && !`${definition.name} ${definition.code} ${definition.module}`.toLowerCase().includes(needle)) continue
    const items = grouped.get(definition.module) ?? []
    items.push(definition)
    grouped.set(definition.module, items)
  }
  const modules = [...grouped.keys()].sort((left, right) => {
    const leftIndex = moduleOrder.indexOf(left)
    const rightIndex = moduleOrder.indexOf(right)
    const leftOrder = leftIndex === -1 ? moduleOrder.length : leftIndex
    const rightOrder = rightIndex === -1 ? moduleOrder.length : rightIndex
    return leftOrder - rightOrder || left.localeCompare(right, 'zh-CN')
  })
  const toggle = (codes: PermissionCode[], checked: boolean) => {
    if (checked) {
      const next = permissionClosure([...value, ...codes])
      const toggled = new Set(codes)
      const addedDependencies = next.filter((code) => !selected.has(code) && !toggled.has(code))
      if (addedDependencies.length) {
        modal.confirm({
          title: '同时加入必要的前置权限',
          content: (
            <ul>
              {addedDependencies.map((code) => (
                <li key={code}>{permissionByCode.get(code)?.name}</li>
              ))}
            </ul>
          ),
          okText: '加入并勾选',
          cancelText: '取消',
          onOk: () => onChange?.(next)
        })
      } else onChange?.(next)
      return
    }
    const next = removePermissionDependents(value, codes)
    const retained = new Set(next)
    const removed = new Set(codes)
    const affected = value.filter((code) => !retained.has(code) && !removed.has(code))
    if (affected.length) {
      modal.confirm({
        title: '取消会影响关联权限',
        content: (
          <div>
            <p>以下页面或操作也将取消：</p>
            <ul>
              {affected.map((code) => (
                <li key={code}>{permissionByCode.get(code)?.name}</li>
              ))}
            </ul>
          </div>
        ),
        okText: '一起取消',
        cancelText: '保留',
        onOk: () => onChange?.(next)
      })
    } else onChange?.(next)
  }
  const visibleModule = grouped.has(activeModule) ? activeModule : modules[0]
  const visibleEntries = grouped.get(visibleModule) ?? []
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 pb-4">
        <Input
          type="search"
          disabled={false}
          aria-label="搜索权限"
          prefix={<Search className="text-content-soft size-4" />}
          className="w-full! sm:max-w-sm"
          placeholder="搜索模块、权限名称或编码"
          allowClear
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />
        <span className="text-content-muted flex items-center gap-2 text-sm" aria-live="polite">
          <ShieldCheck className="text-accent size-4" />
          已选 <strong className="text-content tabular-nums">{value.length}</strong> / {definitions.length} 项权限
        </span>
      </div>
      <div className="border-border overflow-hidden rounded-lg border">
        {!grouped.size && <Empty className="py-12" description="没有匹配的权限，请尝试其他关键词" />}
        {!!grouped.size && (
          <div className="grid min-w-0 md:grid-cols-[11rem_minmax(0,1fr)]">
            <nav
              aria-label="权限模块"
              className="bg-surface-muted border-border border-b p-2 md:border-r md:border-b-0"
            >
              <div className="text-content-soft hidden px-3 py-2 text-xs md:block">业务模块 · {grouped.size}</div>
              <div className="flex gap-1 overflow-x-auto md:max-h-96 md:flex-col md:overflow-y-auto">
                {modules.map((module) => (
                  <button
                    key={module}
                    type="button"
                    aria-current={module === visibleModule ? 'true' : undefined}
                    onClick={() => setActiveModule(module)}
                    className={cn(
                      'focus-visible:outline-accent flex shrink-0 items-center justify-between gap-4 rounded-md px-3 py-2.5 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
                      module === visibleModule
                        ? 'bg-accent/10 text-content font-medium'
                        : 'text-content-muted hover:bg-surface'
                    )}
                  >
                    <span>{module}</span>
                    <span
                      className={cn(
                        'text-xs tabular-nums',
                        module === visibleModule ? 'text-accent' : 'text-content-soft'
                      )}
                    >
                      {grouped.get(module)?.filter((entry) => selected.has(entry.code as PermissionCode)).length}/
                      {grouped.get(module)?.length}
                    </span>
                  </button>
                ))}
              </div>
            </nav>
            <PermissionModule
              module={visibleModule}
              entries={visibleEntries}
              selected={selected}
              disabled={disabled}
              protectedRole={protectedRole}
              searching={!!needle}
              onToggle={toggle}
            />
          </div>
        )}
      </div>
      <p className="text-content-soft mt-3 mb-0 text-xs leading-5">
        {protectedRole
          ? '系统自动授予全部权限，包含后续新增权限。此角色仅支持查看。'
          : '页面入口与业务操作独立配置；选择时会检查前置权限。全选仅包含当前权限，不包含未来新增项。'}
      </p>
    </div>
  )
}
